import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { GroupData } from '@everyboard/games';
import { Coord } from '@everyboard/games';
import { PlayerNumberMap } from '@everyboard/games';
import { TableUtils } from '@everyboard/games';
import { TriangularCheckerBoard } from '@everyboard/games';
import { Debug } from '@everyboard/games';
import { ScoreName } from '@everyboard/games';
import { GoLegalityInformation } from '@everyboard/games/triangular-go';
import { GoMove } from '@everyboard/games/triangular-go';
import { GoPhase } from '@everyboard/games/triangular-go';
import { GoPiece } from '@everyboard/games/triangular-go';
import { GoState } from '@everyboard/games/triangular-go';
import { TriangularGoHeuristic } from '@everyboard/games/triangular-go';
import { TriangularGoMoveGenerator } from '@everyboard/games/triangular-go';
import { TriangularGoConfig, TriangularGoRules } from '@everyboard/games/triangular-go';
import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';

import { ViewBox } from '../../../components/game-components/GameComponentUtils';
import { ClickHandler } from '../../../components/game-components/game-component/ClickHandler';
import { TriangularGameComponent } from '../../../components/game-components/game-component/TriangularGameComponent';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-triangular-go',
    templateUrl: './triangular-go.component.html',
    styleUrls: ['../../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass],
})
@Debug.log
export class TriangularGoComponent extends TriangularGameComponent<TriangularGoRules,
                                                                   GoMove,
                                                                   GoState,
                                                                   GoPiece,
                                                                   TriangularGoConfig,
                                                                   GoLegalityInformation>
{

    public boardInfo: GroupData<GoPiece>;

    public ko: MGPOptional<Coord> = MGPOptional.empty();

    public last: MGPOptional<Coord> = MGPOptional.empty();

    public captures: Coord[]= [];

    public GoPiece: typeof GoPiece = GoPiece;

    public constructor() {
        super('TriangularGo');
        this.aiConfig = {
            minimax: [{
                id: 'Territory',
                name: $localize`Territory`,
                heuristic: (): TriangularGoHeuristic => new TriangularGoHeuristic(),
                moveGenerator: (): TriangularGoMoveGenerator => new TriangularGoMoveGenerator(),
            }],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): TriangularGoMoveGenerator => new TriangularGoMoveGenerator(),
            }],
        };
        this.encoder = GoMove.encoder;
        this.canPass = true;
        this.scores = MGPOptional.of(PlayerNumberMap.of(0, 0));
    }

    protected override async showLastMove(move: GoMove): Promise<void> {
        this.last = MGPOptional.of(move.coord);
        this.showCaptures();
    }

    public override hideLastMove(): void {
        this.captures = [];
        this.last = MGPOptional.empty();
    }

    protected override computeViewBox(): ViewBox {
        const state: GoState = this.state();
        const leftmostOccupiedX: number = TableUtils.getLeftmostMatchColumn(state.board, GoPiece.isReachable).get();
        const width: number = state.board[0].length;
        const occupiedWidth: number = width - leftmostOccupiedX;
        const abstractSize: number = occupiedWidth / 2;
        const oddnessOffset: number = 0.5 * this.SPACE_SIZE * (occupiedWidth % 2);
        const evennessOffset: number = leftmostOccupiedX * 0.5 * this.SPACE_SIZE;
        return new ViewBox(
            evennessOffset,
            0,
            (this.SPACE_SIZE * abstractSize) + oddnessOffset,
            this.SPACE_SIZE * state.getHeight(),
        ).expandAll(this.STROKE_WIDTH / 2);
    }

    @ClickHandler((coord: Coord) => '#click-' + coord.x + '-' + coord.y)
    public async onClick(coord: Coord): Promise<MGPValidation> {
        const resultlessMove: GoMove = new GoMove(coord.x, coord.y);
        return this.chooseMove(resultlessMove);
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        const state: GoState = this.state();
        const phase: GoPhase = state.phase;

        this.board = state.getCopiedBoard();
        this.updateScores();

        this.ko = state.koCoord;
        this.canPass = phase.allowsPass();
    }

    private updateScores(): void {
        this.scores = MGPOptional.of(this.state().captured);
    }

    protected override getScoreName(): ScoreName {
        return this.state().phase.getScoreName();
    }

    private showCaptures(): void {
        const previousState: GoState = this.getPreviousState();
        this.captures = [];
        for (const coordAndContent of this.state().getCoordsAndContents()) {
            const coord: Coord = coordAndContent.coord;
            const wasOccupied: boolean = previousState.getPieceAt(coord).isOccupied();
            const isEmpty: boolean = this.board[coord.y][coord.x] === GoPiece.EMPTY;
            const isNotKo: boolean = this.ko.equalsValue(coord) === false;
            if (wasOccupied && isEmpty && isNotKo) {
                this.captures.push(coord);
            }
        }
    }

    public override async pass(): Promise<MGPValidation> {
        const phase: GoPhase = this.state().phase;
        if (phase.isPlaying() || phase.isPassed()) {
            return this.onClick(GoMove.PASS.coord);
        }
        Utils.assert(phase.isCounting() || phase.isAccept(),
                     'TriangularGoComponent: pass() must be called only in playing, passed, counting, or accept phases');
        return this.onClick(GoMove.ACCEPT.coord);
    }

    public getPlayerClassAt(coord: Coord): string[] {
        const piece: GoPiece = this.state().getPieceAt(coord);
        const classes: string[] = [];
        if (this.captures.some((c: Coord) => c.equals(coord))) {
            classes.push('captured-fill');
        }
        if (piece.isOccupied()) {
            classes.push(this.getPlayerClass(piece.player));
        }
        return classes;
    }

    public getTerritoryTriangleTransform(coord: Coord): string {
        let y: number;
        if (this.isUpward(coord)) {
            y = 25;
        } else {
            y = 15;
        }
        return this.getSVGTranslation(20, y) + ' scale(0.6)';
    }

    public isUpward(coord: Coord): boolean {
        return TriangularCheckerBoard.isSpaceDark(coord);
    }

    public isDownward(coord: Coord): boolean {
        return TriangularCheckerBoard.isSpaceDark(coord) === false;
    }

    public getKoTranslationAt(koCoord: Coord): string {
        const koTranslationCoord: Coord = this.getKoTranslationCoordAt(koCoord);
        return this.getSVGTranslationAt(koTranslationCoord);
    }

    private getKoTranslationCoordAt(koCoord: Coord): Coord {
        if (this.isUpward(koCoord)) {
            return this.getTriangleTranslationCoord(koCoord).getNext(new Coord(0, 25));
        } else {
            return this.getTriangleTranslationCoord(koCoord);
        }
    }

}
